/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { useEffect } from 'react';

/** Sync the native `title` on a single alert toast based on whether its text is clamped. */
function syncToastTitle(toastEl: HTMLElement): void {
  const body = toastEl.querySelector<HTMLElement>('.alert-toast-message');
  if (!body) {
    return;
  }
  const truncated = body.scrollHeight > body.clientHeight + 1;
  const fullText = body.textContent?.trim() ?? '';
  if (truncated && fullText) {
    if (toastEl.getAttribute('title') !== fullText) {
      toastEl.setAttribute('title', fullText);
    }
  } else if (toastEl.hasAttribute('title')) {
    toastEl.removeAttribute('title');
  }
}

/**
 * Alert toasts have a fixed height so react-toastify's `stacked` collapse
 * effect works. Content past two lines is clamped, which hides long messages.
 * This hook watches the alert-toast container and, whenever a toast's text is
 * truncated, sets a native `title` on it so hovering reveals the full message.
 * Toasts whose text fits get no title (so no redundant tooltip).
 */
export function useAlertToastTooltips(): void {
  useEffect(() => {
    const sync = () => {
      document.querySelectorAll<HTMLElement>('.alert-toast').forEach(syncToastTitle);
    };

    // Scope the observer to the toast wrapper (App renders only the
    // <ToastContainer>s inside `.theme`) and watch node additions/removals
    // only — no characterData — so we don't re-scan on every app-wide text edit.
    const root = document.querySelector('.theme') ?? document.body;
    const observer = new MutationObserver(sync);
    observer.observe(root, { childList: true, subtree: true });
    // Initial pass for toasts already mounted.
    sync();

    return () => observer.disconnect();
  }, []);
}
